import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../../hooks/use-disclosure";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import { PHYSICAL_ASSESSMENT_EXAM_SECTIONS } from "../components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";
import MentalStatusExaminationModal from "../components/form-components/InitialPsychotherapy/modals/MentalStatusExamination";
import RiskAssessmentModal from "../components/form-components/InitialPsychotherapy/modals/RiskAssessmentModal";
import {
  RiskAssessmentSummary,
  SelectedSectionsSummary,
} from "../components/form-components/InitialPsychotherapy/SelectedFormSummary";
import {
  DAP_INTERVENTIONS_SECTIONS,
  DAP_PATIENT_RESPONSE_SECTIONS,
  OTHER_INTERVENTIONS_NOTE_KEY,
} from "../components/form-components/dap-note/DapNoteConstant";
import {
  DapScreeningButton,
  DapScreeningPreview,
} from "../components/form-components/dap-note/DapScreeningAssessments";
import { useDapScreening } from "../components/form-components/dap-note/use-dap-screening";
import InterventionsProvidedModal from "../components/form-components/dap-note/modals/InterventionsProvidedModal";
import PatientResponseModal from "../components/form-components/dap-note/modals/PatientResponseModal";
import PrimaryCareSoapNotePlan from "../components/form-components/primary-note/PrimaryCareSoapNotePlan";

interface DapNoteProps {
  formik: FormikProps<Form2Payload>;
}

/**
 * DAP (Data, Assessment, Plan) Progress Note.
 *
 * The MSE, Risk Assessment, ICD picker, the whole Plan section and the Follow Up
 * & Instructions section are the same components — and the same formik keys —
 * the Psychotherapy and Psychiatric Notes use, so the notes stay in sync.
 * Interventions Provided and Patient Response are DAP specific and follow the
 * same add-button → modal → summary flow.
 */
const DapNote = ({ formik }: DapNoteProps) => {
  const mseDisclosure = useDisclosure();
  const interventionsDisclosure = useDisclosure();
  const patientResponseDisclosure = useDisclosure();
  const riskAssessmentDisclosure = useDisclosure();
  const icdDisclosure = useDisclosure();
  const appointmentDisclosure = useDisclosure();
  const screening = useDapScreening();
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Chief Complaint is captured once by the encounter header above the
            form, so the note body does not repeat it. */}

        {/* ═══════════════ DATA ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Data</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            rows={4}
            name="dataFreeNote"
            placeholder="Data..."
            onBlur={formik.handleBlur}
            value={formik.values?.dataFreeNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Data → Mental Status Examination (MSE) */}
        <Grid item xs={12} md={12}>
          <CustomTitle mb={1}>Mental Status Examination (MSE)</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => mseDisclosure.open()}
            label="Add Mental Status Examination (MSE)"
          />
          <MentalStatusExaminationModal
            disclosureHost={mseDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PHYSICAL_ASSESSMENT_EXAM_SECTIONS}
            overallNoteKey="overallMseNote"
            overallNoteLabel="Overall MSE Note"
          />
        </Grid>

        {/* Data → Interventions Provided */}
        <Grid item xs={12} md={12}>
          <CustomTitle mb={1}>Interventions Provided</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => interventionsDisclosure.open()}
            label="Add Interventions Provided"
          />
          <InterventionsProvidedModal
            disclosureHost={interventionsDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={DAP_INTERVENTIONS_SECTIONS}
            showSectionLabel={false}
            overallNoteKey={OTHER_INTERVENTIONS_NOTE_KEY}
            overallNoteLabel="Other Therapeutic Interventions"
          />
        </Grid>

        {/* Data → Patient Response */}
        <Grid item xs={12} md={12}>
          <CustomTitle mb={1}>Patient Response</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => patientResponseDisclosure.open()}
            label="Add Patient Response"
          />
          <PatientResponseModal
            disclosureHost={patientResponseDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={DAP_PATIENT_RESPONSE_SECTIONS}
            showSectionLabel={false}
          />
        </Grid>

        {/* ═══════════════ ASSESSMENT ═══════════════ */}
        <Flex flexDirection="column" width={"100%"} gap={1} ml={2}>
          <CustomTitle my={1} color={"primary"}>
            Assessment
          </CustomTitle>
          <CustomTextArea
            rows={4}
            variant="outlined"
            name="assessmentFreeNote"
            placeholder="Assessment..."
            onBlur={formik.handleBlur}
            value={formik.values?.assessmentFreeNote || ""}
            onChange={formik.handleChange}
          />

          <Flex gap={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
            <StyleAddButton
              w="120px"
              onClick={() => icdDisclosure.open()}
              label="Add ICD Codes"
            />
            <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
            <DapScreeningButton screening={screening} />
          </Flex>
          {!!formik.values?.assessmentCodes?.length && (
            <Flex flexDirection={"column"} width={"100%"} gap={1}>
              {renderSelectedIcdCodes(
                formik.values?.assessmentCodes ?? [],
                (code) => {
                  formik.setFieldValue("assessmentCodes", [
                    ...(formik.values?.assessmentCodes ?? []).filter(
                      (it) => it.value !== code.value
                    ),
                  ]);
                },
                "20vh"
              )}
            </Flex>
          )}
        </Flex>

        {/* Assessment → Risk Assessment */}
        <Grid item xs={12} md={12}>
          <CustomTitle mb={1}>Risk Assessment:</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => riskAssessmentDisclosure.open()}
            label="Add Risk Assessment"
          />
          <RiskAssessmentModal
            disclosureHost={riskAssessmentDisclosure}
            formik={formik}
          />
          <RiskAssessmentSummary formik={formik} />
        </Grid>

        {/* Assessment → Screening Assessments (added from the button next to
            "Add ICD Codes" above). */}
        <Grid item xs={12} md={12}>
          <CustomTitle mb={1}>Screening Assessments</CustomTitle>
          <DapScreeningPreview screening={screening} />
        </Grid>

        {/* ═══════════════ PLAN ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Plan
          </CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Plan Note"
            rows={3}
            name="planFreeNote"
            placeholder="Plan..."
            onBlur={formik.handleBlur}
            value={formik.values?.planFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <PrimaryCareSoapNotePlan
            formik={formik}
            showSendAssessment
            showAssignEducation
            showTherapies
          />
        </Grid>

        {/* ═══════════════ FOLLOW UP & INSTRUCTIONS ═══════════════
            Same `followUp` key, placeholder and Schedule F/U Appointment flow as
            the Psychiatric Note, so both notes save and print identically. */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Follow Up & Instructions
          </CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            rows={4}
            variant="outlined"
            name="followUp"
            placeholder="Follow Up & Instructions..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUp || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        {hasUpdateAppointment && (
          <Grid item xs={12}>
            <StyleAddButton
              w="120px"
              onClick={() => {
                appointmentDisclosure.open();
              }}
              label="Schedule F/U Appointment"
            />
            {appointmentDisclosure.opened && (
              <NewAppointment
                enablePrefillPatientData
                opened={appointmentDisclosure.opened}
                onClose={appointmentDisclosure.close}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default DapNote;
